import { cn } from '@dreamstack/util/src/cn'
import { GoogleMap, InfoBox, LoadScript, Polygon } from '@react-google-maps/api'
import React, { useState } from 'react'

import { mapStyle as initialMapStyle } from '@dreamstack/feature-components/src/property/map/mapStyle'
import type { LatLng } from '@dreamstack/feature-components/src/property/map/parseGeoLocation'
import { useTranslation } from '@dreamstack/i18n'

import {
  BERLIN_POSITION,
  BG_BORDER_PATH,
} from './BerlinDistrictData/borderDefinition'
import { DistrictData } from './BerlinDistrictData/districtDefinition'

import { TARGET_URLS } from './BerlinDistrictData/districtURLs'

const PlacesTitle: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn('flex font-montserrat font-semibold mb-px-4', className)}
    {...rest}
  />
)
const PlacesTitleText: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => <div className={cn('pt-px-4', className)} {...rest} />
const PlacesContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn(
      'font-montserrat bg-accentroWhite-full px-px-24 py-px-24 border shadow[0_1px_4px_0_rgba(138,138,138,0.5)] absolute top[20px] left-[20px] z-[100]',
      className
    )}
    {...rest}
  />
)
const PlacesItemsContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => <div className={cn('pt-px-16', className)} {...rest} />

import includes from 'lodash/includes'
import {
  BACKGROUND_POLYGON_OPTIONS,
  POLYGON_OPTIONS,
  POLYGON_OPTIONS_SELECTED,
} from './BerlinDistrictData/polygonStyling'

const PREFIX = 'immobilien?locations='

export const BerlinDistrictSelector: React.FC = () => {
  const t = useTranslation()

  const [districtSelectorState, setDistrictSelectorState] = useState<{
    selectedDistricts: string[]
    showInfobox: boolean
    infoBoxPosition: {
      lat?: number | null
      lng?: number | null
    }
  }>({
    selectedDistricts: [],
    showInfobox: false,
    infoBoxPosition: {
      lat: null,
      lng: null,
    },
  })

  const districtLabels = Object.keys(DistrictData).map(
    (districtName: string, key: number) => {
      return (
        <div
          key={key}
          className="flex py-px-4 text-[14px]"
          style={{
            backgroundColor: includes(
              districtSelectorState.selectedDistricts,
              districtName
            )
              ? '#f0f0f0'
              : 'inherit',
          }}
        >
          <button
            onFocus={() => {
              setDistrictSelectorState((oldData) => {
                const newData: typeof oldData = Object.assign({}, oldData)
                newData.selectedDistricts = [districtName]
                return newData
              })
            }}
            onMouseOver={() => {
              setDistrictSelectorState((oldData) => {
                const newData = Object.assign({}, oldData)
                newData.selectedDistricts = [districtName]
                newData.showInfobox = false
                return newData
              })
            }}
            onClick={() => {
              if (TARGET_URLS[districtName]) {
                window.location.assign(PREFIX + TARGET_URLS[districtName])
              } else {
                alert('Unknown district:\n' + districtName)
              }
            }}
          >
            <span
              className="cursor-pointer"
              style={{
                fontWeight: districtSelectorState.selectedDistricts.includes(
                  districtName
                )
                  ? 'bold'
                  : 'inherit',
              }}
            >
              {districtName}
            </span>
          </button>
        </div>
      )
    }
  )

  //TODO: type all the anys
  const holes: any[] = []
  const districts = Object.keys(DistrictData).map((districtName, key) => {
    const arr: any[] = []
    DistrictData[districtName].split(' ').forEach((part: any) => {
      const tmp = part.split(',')
      arr.push({ lat: parseFloat(tmp[1]), lng: parseFloat(tmp[0]) })
    })
    holes.push(arr.reverse())

    return (
      <Polygon
        key={key}
        paths={arr}
        options={
          districtSelectorState.selectedDistricts.indexOf(districtName) === -1
            ? POLYGON_OPTIONS
            : POLYGON_OPTIONS_SELECTED
        }
        onMouseOver={(data) => {
          setDistrictSelectorState((oldData) => {
            const newData = Object.assign({}, oldData)
            newData.selectedDistricts = [districtName]
            newData.showInfobox = true
            newData.infoBoxPosition = {
              lat: data.latLng?.lat(),
              lng: data.latLng?.lng(),
            }
            return newData
          })
        }}
        onMouseOut={() => {
          setDistrictSelectorState((oldData) => {
            const newData = Object.assign({}, oldData)
            newData.selectedDistricts = []
            newData.showInfobox = false
            return newData
          })
        }}
        onClick={() => {
          if (TARGET_URLS[districtName]) {
            window.location.assign(PREFIX + TARGET_URLS[districtName])
          } else {
            alert('Unknown district:\n' + districtName)
          }
        }}
      />
    )
  })

  const backgroundRegions = [BG_BORDER_PATH].concat(holes)

  return (
    <div className="relative">
      <PlacesContainer>
        <PlacesTitle>
          <PlacesTitleText>{t('accentro:selectDistrict')}</PlacesTitleText>
        </PlacesTitle>
        <PlacesItemsContainer>{districtLabels}</PlacesItemsContainer>
      </PlacesContainer>

      <MyMapNonMemo
        center={{ lat: BERLIN_POSITION.lat, lng: BERLIN_POSITION.lng }}
        mapStyle={initialMapStyle}
      >
        <Polygon
          paths={backgroundRegions}
          options={BACKGROUND_POLYGON_OPTIONS}
        />

        {districts}

        {districtSelectorState.showInfobox &&
          districtSelectorState.selectedDistricts.length &&
          districtSelectorState.infoBoxPosition.lat &&
          districtSelectorState.infoBoxPosition.lng && (
            <InfoBox
              position={
                new google.maps.LatLng(
                  districtSelectorState.infoBoxPosition.lat,
                  districtSelectorState.infoBoxPosition.lng
                )
              }
              options={{ closeBoxURL: ``, enableEventPropagation: true }}
            >
              <div
                style={{
                  width: '250px',
                  height: '80px',
                  padding: `12px`,
                  overflow: 'none',
                }}
              >
                <span
                  style={{
                    backgroundColor: 'white',
                    fontSize: '16px',
                    padding: '5px',
                    border: '2px solid lightgray',
                  }}
                >
                  {districtSelectorState.selectedDistricts.toString()}
                </span>
              </div>
            </InfoBox>
          )}
      </MyMapNonMemo>
    </div>
  )
}

interface MyMapNonMemoProps {
  center: LatLng
  mapStyle?: google.maps.MapTypeStyle[] | null
  height?: number
  children?: React.ReactNode
}

const MyMapNonMemo: React.FC<MyMapNonMemoProps> = ({
  center,
  mapStyle,
  height,
  children,
}) => {
  return (
    <div className="h-[667px] google-maps">
      <LoadScript
        googleMapsApiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!}
      >
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={10}
          options={{
            styles: mapStyle,
            mapTypeControl: false,
            rotateControl: false,
            streetViewControl: false,
            scaleControl: true,
            fullscreenControl: false,
          }}
        >
          <>{children}</>
        </GoogleMap>
      </LoadScript>
    </div>
  )
}
